import { Button, Table, message, Space, Modal, Popover } from 'antd';
import * as XLSX from 'xlsx';
import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { Access, useAccess } from 'umi';

export type InsertItem = { productZhName: string; productName: string; questions: { question: string; answer: string; tags: string }[]; name: string; };

const Index: React.FC<{ handleInsertData: (data: InsertItem[]) => Promise<boolean>; }> = ({ handleInsertData }) => {
  const inputRef = useRef<any>(null);
  const [insertData, setInsertData] = useState<InsertItem[]>([]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const access = useAccess();

  useEffect(() => {
    if (insertData.length) {
      setVisible(true);
    }
  }, [insertData]);

  function getTableRowData(rowData: any) {
    const data: any = {};
    [["问题", "question"], ["答案", "answer"], ["标签", "tags"]].forEach((t) => {
      const title = Object.keys(rowData).find(vo => vo.includes(t[0]));
      if (title) {
        data[t[1]] = String(rowData[title] || "").trim();
      }
    })
    return { ...data, answer: data['answer'] || null, tags: data['tags'] ? (data['tags'] as string).replace("/", "-") : '产品-临时' };
  }

  const handleInput = (file: File) => {
    const reader = new FileReader();

    reader.onload = function (e) {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const arr: InsertItem[] = [];
      for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet) as any;
        if (jsonData.length === 0) continue;
        if (!Object.keys(jsonData[0]).some(t => t.includes("问题"))) continue;
        const sheetItems = sheetName.split("-");
        const productZhName = sheetItems[0];
        const productName = sheetItems[sheetItems.length - 1];
        arr.push({
          productZhName,
          productName,
          questions: jsonData.map((vo: any) => getTableRowData(vo)),
          name: `${productName}-${file.name}-${dayjs().format("YYYYMMDDHHmm")}`
        })
      }
      if (arr.length === 0) return message.warning("请检查表格数据格式是否正确！")
      setInsertData(arr);
    }

    reader.readAsBinaryString(file);
  }

  const clear = () => {
    setVisible(false);
    setInsertData([]);
    setLoading(false);
    inputRef.current.value = "";
  }

  return <div>
    <Access accessible={access.productQAEdit}>
      <Popover placement="topRight" title="文件格式要求" content={
        <ul>
          <li>导入的文件的类型为.xlsx文件；</li>
          <li>每张数据表可以有三列，分别对应问题、答案、标签，每列的第一行必须为该列的标题（问题、答案、标签）；</li>
          <li>答案和标签可以不填；</li>
          <li>一个文件中可以有多个工作表，每张工作表的名称请使用[产品中文名-产品简称]，如 小神兽-xss；</li>
        </ul>
      }>
        <div style={{ position: 'relative' }}>
          <Button type="primary">导入问题</Button>
          <input style={{ position: "absolute", left: 0, top: 0, width: '100%', height: '100%', padding: 0, margin: 0, overflow: 'hidden', opacity: 0, cursor: "pointer" }} type="file" id="xlsxFile" accept=".xlsx" onInput={e => handleInput((e.target as any).files[0])} ref={inputRef} />
        </div>
      </Popover>
    </Access>
    <Modal
      visible={visible}
      onCancel={clear}
      width={800}
      footer={<Space>
        <Button onClick={clear}>取消</Button>
        <Button type="primary" loading={loading} onClick={async () => {
          if (loading) return;
          setLoading(true);
          const result = await handleInsertData(insertData);
          if (!result) return;
          setLoading(false);
          clear();
        }}>确定导入</Button>
      </Space>}
    >
      <Space style={{ width: '100%' }} direction="vertical">
        {
          insertData.map(vo => <div key={vo.productName}>
            <h3>产品中文名称：{vo.productZhName} 简称：{vo.productName}</h3>
            <Table
              dataSource={vo.questions}
              scroll={{ y: 600 }}
              columns={[
                { title: '问题', dataIndex: 'question' },
                { title: '答案', dataIndex: 'answer' },
                { title: '标签', dataIndex: 'tags' },
              ]}
              pagination={false}
            />
          </div>)
        }
        <h5>注：若是问题已经存在，会自动剔除！</h5>
      </Space>
    </Modal>
  </div>
}

export default Index;